import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon,
  Plus,
  ChevronLeft,
  ChevronRight,
  Clock,
  Car,
  User,
  Phone,
  Building2,
  MessageSquare,
  Trash2,
  Check,
  X,
  Timer,
  LayoutGrid,
  List,
  Star,
  Pencil
} from "lucide-react";
import { format, addDays, startOfWeek, addWeeks, subWeeks, isSameDay, parseISO, addMinutes, parse } from "date-fns";
import { fr } from "date-fns/locale";

// Créneaux de 30 minutes de 8h à 19h
const generateTimeSlots = () => {
  const slots = [];
  for (let h = 8; h <= 18; h++) {
    slots.push(`${h.toString().padStart(2, '0')}:00`);
    if (h < 18) slots.push(`${h.toString().padStart(2, '0')}:30`);
  }
  return slots;
};

const TIME_SLOTS = generateTimeSlots();

const DURATION_OPTIONS = [
  { value: 30, label: "30 min" },
  { value: 45, label: "45 min" },
  { value: 60, label: "1h" },
  { value: 90, label: "1h30" },
  { value: 120, label: "2h" },
  { value: 150, label: "2h30" },
  { value: 180, label: "3h" },
  { value: 240, label: "4h" },
];

import { getAgencyColorByAgency, AgencyDot } from "@/lib/agencyColors";
import { LoanDialog, LoansBanner, LoansCompactList, loansForDay, emptyLoan } from "@/components/LoanVehicles";
import { KeyRound } from "lucide-react";

export default function PlanningPage() {
  const { user, agencies } = useAuth();
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [selectedAgency, setSelectedAgency] = useState(user?.agency_id || "all");
  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [prestations, setPrestations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [viewMode, setViewMode] = useState("calendar"); // "calendar" ou "list"
  const [showDetailSheet, setShowDetailSheet] = useState(false);
  const [isEditingAppointment, setIsEditingAppointment] = useState(false);
  const [editFormData, setEditFormData] = useState({ date: "", time: "", duration: 60 });
  const [loans, setLoans] = useState([]);
  const [showLoanDialog, setShowLoanDialog] = useState(false);
  const [editingLoan, setEditingLoan] = useState(null);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  const [mobileSelectedDate, setMobileSelectedDate] = useState(new Date());

  // Detect mobile
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const [newAppointment, setNewAppointment] = useState({
    client_id: "",
    client_name: "",
    client_phone: "",
    agency_id: user?.agency_id || (agencies.length > 0 ? agencies[0].id : ""),
    date: format(new Date(), "yyyy-MM-dd"),
    time: "09:00",
    duration: 60,
    prestation: "",
    prestation_detail: "",
    price: 0,
    vehicle: "",
    immatriculation: ""
  });

  // Update agency_id when user or agencies change
  useEffect(() => {
    if (!newAppointment.agency_id && (user?.agency_id || agencies.length > 0)) {
      setNewAppointment(prev => ({
        ...prev,
        agency_id: user?.agency_id || agencies[0]?.id || ""
      }));
    }
  }, [user, agencies]);

  useEffect(() => {
    fetchData();
    fetchPrestations();
  }, [selectedAgency, weekStart]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const agencyParam = selectedAgency !== "all" ? `agency_id=${selectedAgency}` : "";
      
      const [appointmentsRes, clientsRes, loansRes] = await Promise.all([
        axios.get(`${API}/appointments?${agencyParam}`),
        axios.get(`${API}/clients?${agencyParam}`),
        axios.get(`${API}/loans?${agencyParam}&start=${format(weekStart, "yyyy-MM-dd")}&end=${format(addDays(weekStart, 6), "yyyy-MM-dd")}`)
      ]);
      
      setAppointments(appointmentsRes.data);
      setClients(clientsRes.data);
      setLoans(loansRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const fetchPrestations = async () => {
    try {
      const res = await axios.get(`${API}/prestations`);
      setPrestations(res.data.prestations);
    } catch (error) {
      console.error("Failed to fetch prestations:", error);
    }
  };

  const handleCreateAppointment = async () => {
    try {
      if (!newAppointment.client_name || !newAppointment.agency_id || !newAppointment.prestation) {
        toast.error("Veuillez remplir tous les champs obligatoires");
        return;
      }
      if (newAppointment.prestation.includes('Prestation Mécanique') && !newAppointment.prestation_detail?.trim()) {
        toast.error("Décrivez le chantier mécanique");
        return;
      }
      
      await axios.post(`${API}/appointments`, newAppointment);
      toast.success("Rendez-vous créé avec succès");
      setShowAddDialog(false);
      fetchData();
      resetNewAppointment();
    } catch (error) {
      toast.error("Erreur lors de la création du rendez-vous");
    }
  };

  const handleUpdateStatus = async (appointmentId, status) => {
    try {
      await axios.put(`${API}/appointments/${appointmentId}`, { status });
      toast.success("Statut mis à jour");
      fetchData();
      setShowDetailSheet(false);
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    try {
      await axios.delete(`${API}/appointments/${appointmentId}`);
      toast.success("Rendez-vous supprimé");
      fetchData();
      setShowDetailSheet(false);
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleSendSMS = async (appointmentId) => {
    try {
      const res = await axios.post(`${API}/sms/send-reminder/${appointmentId}`);
      if (res.data.success) {
        toast.success("SMS envoyé avec succès");
      } else {
        toast.warning(res.data.message);
      }
    } catch (error) {
      toast.error("Erreur lors de l'envoi du SMS");
    }
  };

  const handleSendReviewRequest = async (appointmentId) => {
    try {
      const res = await axios.post(`${API}/sms/send-review-request/${appointmentId}`);
      if (res.data.success) {
        toast.success("Demande d'avis Google envoyée ! ⭐");
        // Refresh to show the review_request_sent status
        fetchData();
      } else {
        toast.warning(res.data.message);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Erreur lors de l'envoi";
      toast.error(errorMsg);
    }
  };

  const resetNewAppointment = () => {
    setNewAppointment({
      client_id: "",
      client_name: "",
      client_phone: "",
      agency_id: user?.agency_id || "",
      date: format(new Date(), "yyyy-MM-dd"),
      time: "09:00",
      duration: 60,
      prestation: "",
      prestation_detail: "",
      price: 0,
      vehicle: "",
      immatriculation: ""
    });
  };

  const handleClientSelect = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setNewAppointment({
        ...newAppointment,
        client_id: client.id,
        client_name: client.name,
        client_phone: client.phone,
        vehicle: client.vehicle,
        immatriculation: client.immatriculation,
        agency_id: client.agency_id
      });
    }
  };

  const getWeekDays = () => {
    return [...Array(7)].map((_, i) => addDays(weekStart, i));
  };

  // Calculer la position et la hauteur d'un RDV sur la grille
  const getAppointmentStyle = (appointment) => {
    const startTime = appointment.time;
    const duration = appointment.duration || 60;
    
    // Trouver l'index du créneau de début
    const startIndex = TIME_SLOTS.indexOf(startTime);
    if (startIndex === -1) return null;
    
    // Chaque créneau fait 30 minutes, donc la hauteur = (durée / 30) * hauteur d'un créneau
    const slotHeight = 50; // pixels par créneau de 30 min
    const height = (duration / 30) * slotHeight;
    const top = startIndex * slotHeight;
    
    return { top, height, duration };
  };

  // Convertir heure en minutes depuis minuit pour comparaison
  const timeToMinutes = (timeStr) => {
    const [hours, mins] = timeStr.split(':').map(Number);
    return hours * 60 + mins;
  };

  // Vérifier si deux RDV se chevauchent
  const appointmentsOverlap = (apt1, apt2) => {
    const start1 = timeToMinutes(apt1.time);
    const end1 = start1 + (apt1.duration || 60);
    const start2 = timeToMinutes(apt2.time);
    const end2 = start2 + (apt2.duration || 60);
    
    return start1 < end2 && start2 < end1;
  };

  // Calculer les colonnes pour les RDV qui se chevauchent
  const getAppointmentsWithColumns = (dayAppointments) => {
    if (dayAppointments.length === 0) return [];
    
    // Trier par heure de début
    const sorted = [...dayAppointments].sort((a, b) => 
      timeToMinutes(a.time) - timeToMinutes(b.time)
    );
    
    // Grouper les RDV qui se chevauchent
    const groups = [];
    let currentGroup = [sorted[0]];
    
    for (let i = 1; i < sorted.length; i++) {
      const apt = sorted[i];
      // Vérifier si ce RDV chevauche un des RDV du groupe actuel
      const overlapsWithGroup = currentGroup.some(groupApt => 
        appointmentsOverlap(apt, groupApt)
      );
      
      if (overlapsWithGroup) {
        currentGroup.push(apt);
      } else {
        groups.push(currentGroup);
        currentGroup = [apt];
      }
    }
    groups.push(currentGroup);
    
    // Assigner les colonnes dans chaque groupe
    const result = [];
    for (const group of groups) {
      const numCols = group.length;
      group.forEach((apt, index) => {
        result.push({
          ...apt,
          column: index,
          totalColumns: numCols
        });
      });
    }
    
    return result;
  };

  // Obtenir les RDV pour une journée donnée avec colonnes calculées
  const getAppointmentsForDay = (day) => {
    const dateStr = format(day, "yyyy-MM-dd");
    const dayAppointments = appointments.filter(apt => apt.date === dateStr);
    return getAppointmentsWithColumns(dayAppointments);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed": return "bg-green-500 border-green-400";
      case "cancelled": return "bg-red-500/50 border-red-400";
      default: return "bg-[#FEED00]/20 border-[#FEED00]";
    }
  };

  const getStatusTextColor = (status) => {
    switch (status) {
      case "completed": return "text-green-100";
      case "cancelled": return "text-red-200 line-through";
      default: return "text-[#FEED00]";
    }
  };

  // Obtenir la couleur d'une agence par son ID
  const getAgencyColor = (agencyId) => getAgencyColorByAgency(agencies.find(a => a.id === agencyId));

  // Obtenir le nom de l'agence par son ID
  const getAgencyName = (agencyId) => {
    const agency = agencies.find(a => a.id === agencyId);
    return agency?.city || "Inconnu";
  };

  // Couleur du RDV basée sur l'agence (quand "Toutes" est sélectionné) ou le statut
  const getAppointmentColor = (apt) => {
    if (apt.status === "completed") {
      return { bg: 'rgba(34, 197, 94, 0.3)', border: '#22C55E', text: '#4ADE80' };
    }
    if (apt.status === "cancelled") {
      return { bg: 'rgba(239, 68, 68, 0.3)', border: '#EF4444', text: '#FCA5A5' };
    }
    return getAgencyColor(apt.agency_id);
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes}min`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h${m}` : `${h}h`;
  };

  const getEndTime = (startTime, duration) => {
    const [hours, mins] = startTime.split(':').map(Number);
    const startDate = new Date();
    startDate.setHours(hours, mins, 0, 0);
    const endDate = addMinutes(startDate, duration);
    return format(endDate, 'HH:mm');
  };

  const openNewLoan = (day = new Date()) => {
    const agencyId = selectedAgency !== "all" ? selectedAgency : (user?.agency_id || (user?.agency_ids?.[0]) || agencies[0]?.id || "");
    setEditingLoan(emptyLoan(agencyId, day));
    setShowLoanDialog(true);
  };

  const openLoan = (loan) => {
    setEditingLoan({ ...loan });
    setShowLoanDialog(true);
  };

  // Fonction pour mettre à jour un RDV (tous les champs)
  const handleUpdateAppointment = async () => {
    if (!selectedAppointment) return;
    if (!editFormData.client_name || !editFormData.prestation || !editFormData.vehicle) {
      toast.error("Client, véhicule et prestation sont obligatoires");
      return;
    }
    
    try {
      const payload = {
        client_name: editFormData.client_name,
        client_phone: editFormData.client_phone,
        vehicle: editFormData.vehicle,
        immatriculation: editFormData.immatriculation,
        prestation: editFormData.prestation,
        prestation_detail: editFormData.prestation_detail || null,
        date: editFormData.date,
        time: editFormData.time,
        duration: editFormData.duration,
        ...(user?.role !== "mecano" ? { price: parseFloat(editFormData.price) || 0 } : {})
      };
      const res = await axios.put(`${API}/appointments/${selectedAppointment.id}`, payload);
      
      toast.success("RDV modifié avec succès");
      setIsEditingAppointment(false);
      fetchData();
      setSelectedAppointment(res.data);
    } catch (error) {
      toast.error("Erreur lors de la modification");
    }
  };

  // Démarrer l'édition d'un RDV
  const startEditingAppointment = () => {
    if (selectedAppointment) {
      setEditFormData({
        client_name: selectedAppointment.client_name || "",
        client_phone: selectedAppointment.client_phone || "",
        vehicle: selectedAppointment.vehicle || "",
        immatriculation: selectedAppointment.immatriculation || "",
        prestation: selectedAppointment.prestation || "",
        prestation_detail: selectedAppointment.prestation_detail || "",
        price: selectedAppointment.price ?? 0,
        date: selectedAppointment.date,
        time: selectedAppointment.time,
        duration: selectedAppointment.duration || 60
      });
      setIsEditingAppointment(true);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 
            className="text-2xl sm:text-3xl font-bold"
            style={{ fontFamily: 'Bebas Neue, sans-serif', color: '#FEED00', letterSpacing: '0.05em' }}
          >
            PLANNING
          </h1>
          <p className="text-gray-500 mt-1">
            Semaine du {format(weekStart, "d MMMM", { locale: fr })} au {format(addDays(weekStart, 6), "d MMMM yyyy", { locale: fr })}
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
          {(user?.role === "admin" || user?.role === "associe") && (
            <Select value={selectedAgency} onValueChange={setSelectedAgency}>
              <SelectTrigger className="w-[160px] bg-black border-gray-800 text-white" data-testid="planning-agency-filter">
                <Building2 className="w-4 h-4 mr-2 text-gray-600" />
                <SelectValue placeholder="Agence" />
              </SelectTrigger>
              <SelectContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}>
                <SelectItem value="all" className="text-gray-200">Toutes</SelectItem>
                {agencies.map((agency) => (
                  <SelectItem key={agency.id} value={agency.id} className="text-gray-200">
                    <span className="flex items-center gap-2">
                      <AgencyDot color={getAgencyColorByAgency(agency)} />
                      {agency.city}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <div className="flex items-center gap-1 bg-black border border-gray-800 rounded-md p-1">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setWeekStart(subWeeks(weekStart, 1))}
              className="text-gray-400 hover:text-[#FEED00] hover:bg-[#FEED00]/10"
              data-testid="prev-week-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button 
              variant="ghost"
              onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}
              className="text-gray-400 hover:text-[#FEED00] hover:bg-[#FEED00]/10 text-sm"
              data-testid="today-btn"
            >
              Aujourd'hui
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setWeekStart(addWeeks(weekStart, 1))}
              className="text-gray-400 hover:text-[#FEED00] hover:bg-[#FEED00]/10"
              data-testid="next-week-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Switch Vue Calendrier / Liste */}
          <div className="flex items-center bg-black border border-gray-800 rounded-md p-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode("calendar")}
              className={`px-3 h-8 ${viewMode === "calendar" ? "bg-[#FEED00] text-black hover:bg-[#FEED00]" : "text-gray-400 hover:text-white"}`}
              data-testid="view-calendar-btn"
            >
              <LayoutGrid className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Calendrier</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode("list")}
              className={`px-3 h-8 ${viewMode === "list" ? "bg-[#FEED00] text-black hover:bg-[#FEED00]" : "text-gray-400 hover:text-white"}`}
              data-testid="view-list-btn"
            >
              <List className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Liste</span>
            </Button>
          </div>

          <Button
            variant="outline"
            onClick={() => openNewLoan()}
            className="border-gray-700 text-gray-200 hover:border-[#FEED00] hover:text-[#FEED00]"
            data-testid="add-loan-btn"
          >
            <KeyRound className="w-4 h-4 mr-2" />
            Véhicule de prêt
          </Button>

          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button style={{ background: '#FEED00', color: '#000' }} data-testid="add-appointment-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau RDV
              </Button>
            </DialogTrigger>
            <DialogContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }} className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Bebas Neue, sans-serif', color: '#FEED00', letterSpacing: '0.05em' }}>
                  NOUVEAU RENDEZ-VOUS
                </DialogTitle>
                <DialogDescription className="text-gray-500" data-testid="new-appointment-description">
                  {newAppointment.date && newAppointment.time
                    ? `${format(new Date(newAppointment.date + "T00:00:00"), "EEEE d MMMM yyyy", { locale: fr })} à ${newAppointment.time}`
                    : "Créer un nouveau rendez-vous pour un client"}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                {/* Client existant ou nouveau */}
                <div className="space-y-2">
                  <Label className="text-gray-300">Client existant</Label>
                  <Select onValueChange={handleClientSelect}>
                    <SelectTrigger className="bg-black border-gray-800 text-white">
                      <SelectValue placeholder="Sélectionner un client existant..." />
                    </SelectTrigger>
                    <SelectContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }} className="max-h-48">
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id} className="text-gray-200">
                          {client.name} - {client.vehicle}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-gray-800" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-[#0A0A0A] px-2 text-gray-600">ou nouveau client</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Nom *</Label>
                    <Input
                      value={newAppointment.client_name}
                      onChange={(e) => setNewAppointment({ ...newAppointment, client_name: e.target.value })}
                      className="bg-black border-gray-800 text-white"
                      data-testid="new-apt-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Téléphone *</Label>
                    <Input
                      value={newAppointment.client_phone}
                      onChange={(e) => setNewAppointment({ ...newAppointment, client_phone: e.target.value })}
                      placeholder="+33..."
                      className="bg-black border-gray-800 text-white"
                      data-testid="new-apt-phone"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Véhicule *</Label>
                    <Input
                      value={newAppointment.vehicle}
                      onChange={(e) => setNewAppointment({ ...newAppointment, vehicle: e.target.value })}
                      placeholder="BMW 320d"
                      className="bg-black border-gray-800 text-white"
                      data-testid="new-apt-vehicle"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Immatriculation *</Label>
                    <Input
                      value={newAppointment.immatriculation}
                      onChange={(e) => setNewAppointment({ ...newAppointment, immatriculation: e.target.value.toUpperCase() })}
                      placeholder="AB-123-CD"
                      className="bg-black border-gray-800 text-white font-mono"
                      data-testid="new-apt-immat"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Agence *</Label>
                  <Select 
                    value={newAppointment.agency_id} 
                    onValueChange={(v) => setNewAppointment({ ...newAppointment, agency_id: v })}
                  >
                    <SelectTrigger className="bg-black border-gray-800 text-white" data-testid="new-apt-agency">
                      <SelectValue placeholder="Sélectionner une agence" />
                    </SelectTrigger>
                    <SelectContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}>
                      {agencies && agencies.length > 0 ? (
                        agencies.map((agency) => (
                          <SelectItem key={agency.id} value={agency.id} className="text-gray-200">
                            {agency.city}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="p-2 text-gray-500 text-sm">Chargement des agences...</div>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Date *</Label>
                    <Input
                      type="date"
                      value={newAppointment.date}
                      onChange={(e) => setNewAppointment({ ...newAppointment, date: e.target.value })}
                      className="bg-black border-gray-800 text-white"
                      data-testid="new-apt-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Heure *</Label>
                    <Select 
                      value={newAppointment.time} 
                      onValueChange={(v) => setNewAppointment({ ...newAppointment, time: v })}
                    >
                      <SelectTrigger className="bg-black border-gray-800 text-white" data-testid="new-apt-time">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }} className="max-h-48">
                        {TIME_SLOTS.map((time) => (
                          <SelectItem key={time} value={time} className="text-gray-200">{time}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Durée *</Label>
                    <Select 
                      value={newAppointment.duration.toString()} 
                      onValueChange={(v) => setNewAppointment({ ...newAppointment, duration: parseInt(v) })}
                    >
                      <SelectTrigger className="bg-black border-gray-800 text-white" data-testid="new-apt-duration">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}>
                        {DURATION_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value.toString()} className="text-gray-200">
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Prestation(s) * <span className="text-gray-500 text-xs">(plusieurs choix possibles)</span></Label>
                  <div 
                    className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2"
                    style={{ background: '#000', borderColor: '#333' }}
                  >
                    {prestations.map((p) => {
                      const selectedPrestations = newAppointment.prestation ? newAppointment.prestation.split(' + ') : [];
                      const isChecked = selectedPrestations.includes(p);
                      return (
                        <label 
                          key={p} 
                          className="flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-white/5 transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              let updated;
                              if (e.target.checked) {
                                updated = [...selectedPrestations, p];
                              } else {
                                updated = selectedPrestations.filter(sp => sp !== p);
                              }
                              setNewAppointment({ ...newAppointment, prestation: updated.join(' + ') });
                            }}
                            className="w-4 h-4 rounded border-gray-600 bg-black text-[#FEED00] focus:ring-[#FEED00] focus:ring-offset-0"
                            data-testid={`prestation-checkbox-${p.replace(/\s+/g, '-').toLowerCase()}`}
                          />
                          <span className="text-gray-200 text-sm">{p}</span>
                        </label>
                      );
                    })}
                  </div>
                  {newAppointment.prestation && (
                    <p className="text-xs text-[#FEED00] mt-1">
                      Sélectionné : {newAppointment.prestation}
                    </p>
                  )}
                </div>

                {/* Champ détail pour Prestation Mécanique */}
                {newAppointment.prestation && newAppointment.prestation.includes('Prestation Mécanique') && (
                  <div className="space-y-2 p-3 rounded-lg border" style={{ borderColor: 'rgba(254,237,0,0.35)', background: 'rgba(254,237,0,0.05)' }}>
                    <Label className="text-[#FEED00]">Description du chantier mécanique *</Label>
                    <Textarea
                      value={newAppointment.prestation_detail}
                      onChange={(e) => setNewAppointment({ ...newAppointment, prestation_detail: e.target.value })}
                      placeholder="Décrivez le travail à faire : pièces, symptômes, consignes… Ex: Plaquettes + disques AV, bruit au freinage, pièces fournies par le client"
                      rows={4}
                      className="bg-black border-gray-800 text-white resize-y"
                      data-testid="new-apt-prestation-detail"
                    />
                  </div>
                )}

                {/* Prix caché pour les mécanos */}
                {user?.role !== "mecano" && (
                  <div className="space-y-2">
                    <Label className="text-gray-300">Prix (€) *</Label>
                    <Input
                      type="number"
                      value={newAppointment.price === 0 ? "" : newAppointment.price}
                      onChange={(e) => setNewAppointment({ ...newAppointment, price: e.target.value === "" ? 0 : parseFloat(e.target.value) })}
                      placeholder="0"
                      className="bg-black border-gray-800 text-white"
                      data-testid="new-apt-price"
                    />
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowAddDialog(false)}
                    className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800"
                  >
                    Annuler
                  </Button>
                  <Button 
                    onClick={handleCreateAppointment}
                    className="flex-1"
                    style={{ background: '#FEED00', color: '#000' }}
                    data-testid="confirm-new-apt-btn"
                  >
                    Créer le RDV
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Vue Calendrier */}
      {viewMode === "calendar" && (
        <Card style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }} className="overflow-hidden">
          {/* Légende des couleurs par agence */}
          {(user?.role === "admin" || user?.role === "associe") && agencies.length > 1 && (
            <div className="px-4 py-3 border-b flex flex-wrap items-center gap-4" style={{ borderColor: '#1A1A1A', background: '#111' }} data-testid="planning-agency-legend">
              <span className="text-xs text-gray-500 font-medium">LÉGENDE :</span>
              {agencies.map((agency) => {
                const color = getAgencyColorByAgency(agency);
                return (
                  <div key={agency.id} className="flex items-center gap-2">
                    <AgencyDot color={color} className="w-3 h-3" />
                    <span className="text-xs" style={{ color: color.text }}>{agency.city}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Vue Mobile - Jour par jour */}
          {isMobile ? (
            <div className="p-4">
              {/* Sélecteur de jour sur mobile */}
              <div className="flex items-center justify-between mb-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMobileSelectedDate(addDays(mobileSelectedDate, -1))}
                  className="text-gray-400 hover:text-white"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <div className="text-center">
                  <p className="text-lg font-bold text-white" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                    {format(mobileSelectedDate, "EEEE", { locale: fr })}
                  </p>
                  <p className="text-2xl font-bold" style={{ color: '#FEED00', fontFamily: 'Bebas Neue, sans-serif' }}>
                    {format(mobileSelectedDate, "d MMMM", { locale: fr })}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMobileSelectedDate(addDays(mobileSelectedDate, 1))}
                  className="text-gray-400 hover:text-white"
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>

              {/* Bouton Aujourd'hui */}
              {!isSameDay(mobileSelectedDate, new Date()) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setMobileSelectedDate(new Date())}
                  className="w-full mb-4 border-[#FEED00]/30 text-[#FEED00] hover:bg-[#FEED00]/10"
                >
                  Retour à aujourd'hui
                </Button>
              )}

              {/* Prêts du jour (mobile) */}
              <LoansCompactList loans={loansForDay(loans, mobileSelectedDate)} agencies={agencies} onSelect={openLoan} title="Prêts ce jour" />

              {/* Liste des RDV du jour sélectionné */}
              <div className="space-y-3">
                {getAppointmentsForDay(mobileSelectedDate).length === 0 ? (
                  <div className="text-center py-12">
                    <CalendarIcon className="w-12 h-12 mx-auto text-gray-700 mb-3" />
                    <p className="text-gray-500">Aucun RDV ce jour</p>
                  </div>
                ) : (
                  getAppointmentsForDay(mobileSelectedDate)
                    .sort((a, b) => a.time.localeCompare(b.time))
                    .map((apt) => {
                      const aptColor = getAppointmentColor(apt);
                      return (
                        <button
                          key={apt.id}
                          onClick={() => {
                            setSelectedAppointment(apt);
                            setShowDetailSheet(true);
                          }}
                          className="w-full p-4 rounded-xl border-l-4 text-left transition-all active:scale-[0.98]"
                          style={{
                            background: aptColor.bg,
                            borderColor: aptColor.border,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                          }}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              {/* Heure */}
                              <div className="flex items-center gap-2 mb-2">
                                <Badge style={{ background: 'rgba(0,0,0,0.3)', color: aptColor.text }} className="text-sm font-bold">
                                  {apt.time}
                                </Badge>
                                <span className="text-xs text-gray-400">
                                  → {getEndTime(apt.time, apt.duration || 60)}
                                </span>
                              </div>
                              {/* Client */}
                              <p className="font-bold text-white text-lg truncate">
                                {apt.client_name}
                              </p>
                              {/* Véhicule */}
                              <p className="text-sm text-gray-300 truncate mt-1">
                                {apt.vehicle}
                              </p>
                              {/* Prestation */}
                              <p className="text-xs text-gray-400 truncate mt-1">
                                {apt.prestation}
                              </p>
                              {apt.prestation_detail && (
                                <p className="text-xs text-[#FEED00]/80 italic mt-0.5 line-clamp-2">→ {apt.prestation_detail}</p>
                              )}
                            </div>
                            {/* Prix (si pas mécano) */}
                            {user?.role !== "mecano" && (
                              <div className="text-right">
                                <p className="text-xl font-bold text-green-400" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                                  {apt.price}€
                                </p>
                              </div>
                            )}
                          </div>
                          {/* Agence */}
                          {selectedAgency === "all" && (
                            <div className="mt-3 pt-2 border-t border-white/10">
                              <span className="text-xs font-medium" style={{ color: aptColor.text }}>
                                📍 {getAgencyName(apt.agency_id)}
                              </span>
                            </div>
                          )}
                        </button>
                      );
                    })
                )}
              </div>
            </div>
          ) : (
            /* Vue Desktop - Calendrier hebdomadaire */
            <>
          <div className="overflow-x-auto">
          <div className="min-w-[900px]">
            {/* Header Row - Jours de la semaine */}
            <div className="grid grid-cols-8 border-b" style={{ borderColor: '#1A1A1A' }}>
              <div className="p-3 bg-black text-gray-600 text-sm font-medium text-center">
                <Clock className="w-4 h-4 mx-auto" />
              </div>
              {getWeekDays().map((day, i) => {
                const isToday = isSameDay(day, new Date());
                const dayAppointments = getAppointmentsForDay(day);
                return (
                  <div 
                    key={i}
                    className={`p-3 text-center border-l ${isToday ? 'bg-[#FEED00]/5' : 'bg-black'}`}
                    style={{ borderColor: '#1A1A1A' }}
                  >
                    <p className="text-xs text-gray-600 uppercase">
                      {format(day, "EEE", { locale: fr })}
                    </p>
                    <p className={`text-xl font-bold ${isToday ? 'text-[#FEED00]' : 'text-white'}`} style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                      {format(day, "d")}
                    </p>
                    {dayAppointments.length > 0 && (
                      <Badge variant="outline" className="mt-1 text-xs" style={{ borderColor: '#FEED00', color: '#FEED00' }}>
                        {dayAppointments.length} RDV
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Bandeau véhicules de prêt */}
            <LoansBanner days={getWeekDays()} loans={loans} agencies={agencies} onSelect={openLoan} onAdd={openNewLoan} />

            {/* Grid Body - Créneaux horaires avec RDV positionnés */}
            <div className="grid grid-cols-8">
              {/* Colonne des heures */}
              <div className="bg-black">
                {TIME_SLOTS.map((time, index) => (
                  <div 
                    key={time} 
                    className="h-[50px] flex items-center justify-center text-xs text-gray-600 border-b"
                    style={{ borderColor: '#1A1A1A' }}
                  >
                    {time}
                  </div>
                ))}
              </div>

              {/* Colonnes des jours avec RDV */}
              {getWeekDays().map((day, dayIndex) => {
                const isToday = isSameDay(day, new Date());
                const dayAppointments = getAppointmentsForDay(day);
                
                return (
                  <div 
                    key={dayIndex}
                    className={`relative border-l ${isToday ? 'bg-[#FEED00]/5' : ''}`}
                    style={{ borderColor: '#1A1A1A' }}
                  >
                    {/* Lignes de créneaux (fond) - avec drop zones */}
                    {TIME_SLOTS.map((time, index) => (
                      <div 
                        key={time} 
                        className="h-[50px] border-b hover:bg-white/5 transition-colors cursor-pointer"
                        style={{ borderColor: '#1A1A1A' }}
                        title={`Nouveau RDV le ${format(day, "dd/MM")} à ${time}`}
                        data-testid={`slot-${format(day, "yyyy-MM-dd")}-${time}`}
                        onClick={() => {
                          setNewAppointment(prev => ({
                            ...prev,
                            date: format(day, "yyyy-MM-dd"),
                            time,
                            agency_id: selectedAgency !== "all" ? selectedAgency : (prev.agency_id || user?.agency_id || agencies[0]?.id || "")
                          }));
                          setShowAddDialog(true);
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.currentTarget.style.backgroundColor = 'rgba(254, 237, 0, 0.15)';
                        }}
                        onDragLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '';
                        }}
                        onDrop={async (e) => {
                          e.preventDefault();
                          e.currentTarget.style.backgroundColor = '';
                          const aptId = e.dataTransfer.getData("appointmentId");
                          const aptData = JSON.parse(e.dataTransfer.getData("appointmentData"));
                          const newDate = format(day, "yyyy-MM-dd");
                          const newTime = time;
                          
                          if (aptId && (aptData.date !== newDate || aptData.time !== newTime)) {
                            try {
                              await axios.put(`${API}/appointments/${aptId}`, {
                                ...aptData,
                                date: newDate,
                                time: newTime
                              }, {
                                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
                              });
                              toast.success(`RDV déplacé à ${newTime}`);
                              fetchData();
                            } catch (error) {
                              toast.error("Erreur lors du déplacement");
                            }
                          }
                        }}
                      />
                    ))}
                    
                    {/* RDV positionnés par-dessus - avec drag & drop */}
                    {dayAppointments.map((apt) => {
                      const style = getAppointmentStyle(apt);
                      if (!style) return null;
                      
                      const aptColor = getAppointmentColor(apt);
                      const isAllAgencies = selectedAgency === "all";
                      
                      return (
                        <button
                          key={apt.id}
                          draggable="true"
                          onDragStart={(e) => {
                            e.dataTransfer.setData("appointmentId", apt.id);
                            e.dataTransfer.setData("appointmentData", JSON.stringify(apt));
                            e.currentTarget.style.opacity = "0.5";
                          }}
                          onDragEnd={(e) => {
                            e.currentTarget.style.opacity = "1";
                          }}
                          onClick={() => {
                            setSelectedAppointment(apt);
                            setShowDetailSheet(true);
                          }}
                          className="absolute rounded-lg border-l-4 px-2 py-1 transition-all hover:shadow-xl hover:z-20 hover:scale-[1.02] cursor-grab active:cursor-grabbing"
                          style={{ 
                            top: `${style.top}px`, 
                            height: `${style.height - 4}px`,
                            minHeight: '48px',
                            overflow: 'hidden',
                            left: apt.totalColumns > 1 ? `${(apt.column / apt.totalColumns) * 100}%` : '3px',
                            right: apt.totalColumns > 1 ? `${((apt.totalColumns - apt.column - 1) / apt.totalColumns) * 100}%` : '3px',
                            width: apt.totalColumns > 1 ? `calc(${100 / apt.totalColumns}% - 6px)` : 'auto',
                            backgroundColor: aptColor.bg,
                            borderColor: aptColor.border,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                          }}
                          data-testid={`calendar-apt-${apt.id}`}
                        >
                          <div className="text-left h-full overflow-hidden flex flex-col">
                            {/* Heure - toujours visible */}
                            <p className="text-[10px] font-bold leading-none" style={{ color: aptColor.text }}>
                              {apt.time}
                            </p>
                            {/* Nom client - en gras, plus lisible */}
                            <p className="font-bold text-xs truncate text-white leading-tight mt-0.5">
                              {apt.client_name.split(' ')[0]}
                            </p>
                            {/* Infos supplémentaires si assez de place */}
                            {style.height > 80 && (
                              <p className="text-[10px] truncate opacity-80 text-gray-200 leading-none mt-1">
                                {apt.vehicle?.split(' ').slice(0,2).join(' ')}
                              </p>
                            )}
                            {style.height > 100 && (
                              <p className="text-[9px] truncate opacity-70 text-gray-300 leading-none mt-0.5">
                                {apt.prestation?.split(' + ')[0]}
                              </p>
                            )}
                            {/* Agence si vue multi-agences */}
                            {isAllAgencies && style.height > 85 && (
                              <p className="text-[9px] font-semibold truncate leading-none mt-auto" style={{ color: aptColor.text }}>
                                {getAgencyName(apt.agency_id)}
                              </p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
            </>
          )}
        </Card>
      )}

      {/* Vue Liste */}
      {viewMode === "list" && (
        <Card style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}>
          {/* Légende des couleurs par agence en vue liste */}
          {(user?.role === "admin" || user?.role === "associe") && agencies.length > 1 && (
            <div className="px-4 py-3 border-b flex flex-wrap items-center gap-4" style={{ borderColor: '#1A1A1A', background: '#111' }} data-testid="planning-list-agency-legend">
              <span className="text-xs text-gray-500 font-medium">LÉGENDE :</span>
              {agencies.map((agency) => {
                const color = getAgencyColorByAgency(agency);
                return (
                  <div key={agency.id} className="flex items-center gap-2">
                    <AgencyDot color={color} className="w-3 h-3" />
                    <span className="text-xs" style={{ color: color.text }}>{agency.city}</span>
                  </div>
                );
              })}
            </div>
          )}
          <CardContent className="p-0">
            {loans.length > 0 && (
              <div className="p-3 border-b" style={{ borderColor: '#1A1A1A' }}>
                <LoansCompactList loans={loans} agencies={agencies} onSelect={openLoan} title="Véhicules de prêt cette semaine" />
              </div>
            )}
            {getWeekDays().map((day, dayIndex) => {
              const dayAppointments = getAppointmentsForDay(day);
              const isToday = isSameDay(day, new Date());
              
              if (dayAppointments.length === 0) return null;
              
              return (
                <div key={dayIndex} className="border-b" style={{ borderColor: '#1A1A1A' }}>
                  <div className={`px-4 py-3 ${isToday ? 'bg-[#FEED00]/10' : 'bg-black'}`} style={{ borderBottom: '1px solid #1A1A1A' }}>
                    <p className={`font-bold ${isToday ? 'text-[#FEED00]' : 'text-white'}`} style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                      {format(day, "EEEE d MMMM", { locale: fr }).toUpperCase()}
                    </p>
                    <p className="text-xs text-gray-500">{dayAppointments.length} rendez-vous</p>
                  </div>
                  
                  <div className="divide-y" style={{ borderColor: '#1A1A1A' }}>
                    {dayAppointments
                      .sort((a, b) => a.time.localeCompare(b.time))
                      .map((apt) => {
                        const aptColor = getAppointmentColor(apt);
                        const isAllAgencies = selectedAgency === "all";
                        
                        return (
                          <button
                            key={apt.id}
                            onClick={() => {
                              setSelectedAppointment(apt);
                              setShowDetailSheet(true);
                            }}
                            className="w-full px-4 py-3 flex items-center gap-4 hover:bg-white/5 transition-colors text-left"
                            data-testid={`list-apt-${apt.id}`}
                          >
                            <div className="text-center min-w-[60px]">
                              <p className="text-lg font-bold" style={{ fontFamily: 'Bebas Neue, sans-serif', color: aptColor.text }}>
                                {apt.time}
                              </p>
                              <p className="text-xs text-gray-500">{formatDuration(apt.duration || 60)}</p>
                            </div>
                            
                            <div 
                              className="w-1 h-12 rounded-full" 
                              style={{ backgroundColor: aptColor.border }}
                            />
                            
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-white truncate">{apt.client_name}</p>
                              <div className="flex items-center gap-3 text-xs text-gray-400 mt-1">
                                <span className="flex items-center gap-1">
                                  <Car className="w-3 h-3" />
                                  {apt.vehicle}
                                </span>
                                <span className="hidden sm:inline">{apt.immatriculation}</span>
                                {/* Afficher l'agence si toutes sélectionnées */}
                                {isAllAgencies && (
                                  <span className="flex items-center gap-1" style={{ color: aptColor.text }}>
                                    <Building2 className="w-3 h-3" />
                                    {getAgencyName(apt.agency_id)}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            <div className="hidden md:block max-w-[260px]">
                              <Badge style={{ backgroundColor: aptColor.bg, color: aptColor.text, borderColor: aptColor.border }} className="border">
                                {apt.prestation}
                              </Badge>
                              {apt.prestation_detail && (
                                <p className="text-xs text-gray-400 italic mt-1 line-clamp-2" title={apt.prestation_detail}>→ {apt.prestation_detail}</p>
                              )}
                            </div>
                            
                            {user?.role !== "mecano" && (
                              <div className="text-right min-w-[70px]">
                                <p className="font-bold text-green-400" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                                  {apt.price} €
                                </p>
                              </div>
                            )}
                            
                            <div className="hidden sm:block">
                              {apt.status === "completed" && (
                                <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                                  <Check className="w-3 h-3 mr-1" />
                                  Terminé
                                </Badge>
                              )}
                              {apt.status === "cancelled" && (
                                <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                                  <X className="w-3 h-3 mr-1" />
                                  Annulé
                                </Badge>
                              )}
                            </div>
                          </button>
                        );
                      })}
                  </div>
                </div>
              );
            })}
            
            {getWeekDays().every(day => getAppointmentsForDay(day).length === 0) && (
              <div className="p-8 text-center">
                <CalendarIcon className="w-12 h-12 mx-auto text-gray-600 mb-3" />
                <p className="text-gray-400">Aucun rendez-vous cette semaine</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialog véhicule de prêt */}
      <LoanDialog
        open={showLoanDialog}
        onOpenChange={setShowLoanDialog}
        loan={editingLoan}
        setLoan={setEditingLoan}
        agencies={agencies}
        user={user}
        onSaved={fetchData}
      />

      {/* Appointment Detail Sheet */}
      <Sheet open={showDetailSheet} onOpenChange={setShowDetailSheet}>
        <SheetContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }} className="w-full sm:max-w-md overflow-y-auto">
          {selectedAppointment && (
            <>
              <SheetHeader>
                <SheetTitle style={{ fontFamily: 'Bebas Neue, sans-serif', color: '#FEED00', letterSpacing: '0.05em' }} className="flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5" />
                  DÉTAILS DU RDV
                </SheetTitle>
                <SheetDescription className="text-gray-500">
                  {format(parseISO(selectedAppointment.date), "EEEE d MMMM yyyy", { locale: fr })}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Time & Duration - Editable */}
                <div className="p-4 rounded-lg" style={{ background: '#111' }}>
                  {isEditingAppointment ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-400">Modifier le RDV</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setIsEditingAppointment(false)}
                          className="text-gray-500 hover:text-white h-8"
                        >
                          Annuler
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-gray-400 text-xs">Client *</Label>
                          <Input
                            value={editFormData.client_name}
                            onChange={(e) => setEditFormData({ ...editFormData, client_name: e.target.value })}
                            className="bg-black border-gray-800 text-white h-10"
                            data-testid="edit-apt-client-name"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-400 text-xs">Téléphone</Label>
                          <Input
                            value={editFormData.client_phone}
                            onChange={(e) => setEditFormData({ ...editFormData, client_phone: e.target.value })}
                            className="bg-black border-gray-800 text-white h-10"
                            data-testid="edit-apt-client-phone"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-gray-400 text-xs">Véhicule *</Label>
                          <Input
                            value={editFormData.vehicle}
                            onChange={(e) => setEditFormData({ ...editFormData, vehicle: e.target.value })}
                            className="bg-black border-gray-800 text-white h-10"
                            data-testid="edit-apt-vehicle"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-400 text-xs">Immatriculation</Label>
                          <Input
                            value={editFormData.immatriculation}
                            onChange={(e) => setEditFormData({ ...editFormData, immatriculation: e.target.value.toUpperCase() })}
                            className="bg-black border-gray-800 text-white h-10 font-mono"
                            data-testid="edit-apt-immat"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-gray-400 text-xs">Prestation(s) *</Label>
                        <div className="border rounded-md p-2 max-h-36 overflow-y-auto space-y-1 mt-1" style={{ background: '#000', borderColor: '#333' }}>
                          {prestations.map((p) => {
                            const selected = editFormData.prestation ? editFormData.prestation.split(' + ') : [];
                            const checked = selected.includes(p);
                            return (
                              <label key={p} className="flex items-center gap-2 p-1 rounded cursor-pointer hover:bg-white/5">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => {
                                    const updated = e.target.checked ? [...selected, p] : selected.filter(sp => sp !== p);
                                    setEditFormData({ ...editFormData, prestation: updated.join(' + ') });
                                  }}
                                  className="w-4 h-4 rounded border-gray-600 bg-black text-[#FEED00] focus:ring-[#FEED00] focus:ring-offset-0"
                                  data-testid={`edit-prestation-checkbox-${p.replace(/\s+/g, '-').toLowerCase()}`}
                                />
                                <span className="text-gray-200 text-xs">{p}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                      {editFormData.prestation?.includes('Prestation Mécanique') && (
                        <div className="p-2 rounded-lg border" style={{ borderColor: 'rgba(254,237,0,0.35)', background: 'rgba(254,237,0,0.05)' }}>
                          <Label className="text-[#FEED00] text-xs">Description du chantier mécanique</Label>
                          <Textarea
                            value={editFormData.prestation_detail}
                            onChange={(e) => setEditFormData({ ...editFormData, prestation_detail: e.target.value })}
                            rows={4}
                            placeholder="Décrivez le travail à faire : pièces, symptômes, consignes…"
                            className="bg-black border-gray-800 text-white resize-y mt-1"
                            data-testid="edit-apt-prestation-detail"
                          />
                        </div>
                      )}
                      {user?.role !== "mecano" && (
                        <div>
                          <Label className="text-gray-400 text-xs">Prix (€)</Label>
                          <Input
                            type="number"
                            value={editFormData.price}
                            onChange={(e) => setEditFormData({ ...editFormData, price: e.target.value })}
                            className="bg-black border-gray-800 text-white h-10"
                            data-testid="edit-apt-price"
                          />
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-gray-400 text-xs">Date</Label>
                          <Input
                            type="date"
                            value={editFormData.date}
                            onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })}
                            className="bg-black border-gray-800 text-white h-10"
                            data-testid="edit-apt-date"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-400 text-xs">Heure</Label>
                          <Input
                            type="time"
                            value={editFormData.time}
                            onChange={(e) => setEditFormData({ ...editFormData, time: e.target.value })}
                            className="bg-black border-gray-800 text-white h-10"
                            data-testid="edit-apt-time"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-gray-400 text-xs">Durée</Label>
                        <Select 
                          value={String(editFormData.duration)} 
                          onValueChange={(v) => setEditFormData({ ...editFormData, duration: parseInt(v) })}
                        >
                          <SelectTrigger className="bg-black border-gray-800 text-white h-10" data-testid="edit-apt-duration">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}>
                            {DURATION_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={String(opt.value)} className="text-gray-200">
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button 
                        onClick={handleUpdateAppointment}
                        className="w-full h-10"
                        style={{ background: '#FEED00', color: '#000' }}
                        data-testid="edit-apt-save"
                      >
                        Enregistrer les modifications
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5" style={{ color: '#FEED00' }} />
                        <div>
                          <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                            {selectedAppointment.time} - {getEndTime(selectedAppointment.time, selectedAppointment.duration || 60)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge style={{ background: 'rgba(254, 237, 0, 0.2)', color: '#FEED00' }}>
                          <Timer className="w-3 h-3 mr-1" />
                          {formatDuration(selectedAppointment.duration || 60)}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={startEditingAppointment}
                          className="text-gray-500 hover:text-[#FEED00] h-8 px-2"
                          title="Modifier le RDV"
                          data-testid="edit-apt-btn"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Client Info */}
                <div className="p-3 rounded-lg space-y-2" style={{ background: '#111' }}>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-600" />
                    <div>
                      <p className="text-xs text-gray-600">Client</p>
                      <p className="text-white font-medium text-sm">{selectedAppointment.client_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-600" />
                    <div>
                      <p className="text-xs text-gray-600">Téléphone</p>
                      <p className="text-white font-mono text-sm">{selectedAppointment.client_phone}</p>
                    </div>
                  </div>
                </div>

                {/* Vehicle Info */}
                <div className="p-3 rounded-lg space-y-2" style={{ background: '#111' }}>
                  <div className="flex items-center gap-2">
                    <Car className="w-4 h-4 text-gray-600" />
                    <div>
                      <p className="text-xs text-gray-600">Véhicule</p>
                      <p className="text-white text-sm">{selectedAppointment.vehicle}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-4 h-4 flex items-center justify-center text-gray-600 text-xs font-bold">ID</span>
                      <div>
                        <p className="text-xs text-gray-600">Immatriculation</p>
                        <p className="text-white font-mono text-sm">{selectedAppointment.immatriculation}</p>
                      </div>
                    </div>
                    {/* Agence inline */}
                    <div className="text-right">
                      <p className="text-xs text-gray-600">Agence</p>
                      <p className="text-sm font-medium" style={{ color: getAgencyColor(selectedAppointment.agency_id).text }}>
                        {getAgencyName(selectedAppointment.agency_id)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Prestation & Price */}
                <div className="p-3 rounded-lg" style={{ background: '#111' }}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-xs text-gray-600">Prestation</p>
                      <Badge style={{ background: 'rgba(254, 237, 0, 0.2)', color: '#FEED00' }} className="mt-1 text-xs">
                        {selectedAppointment.prestation}
                      </Badge>
                      {/* Détail prestation mécanique */}
                      {selectedAppointment.prestation_detail && (
                        <p className="text-sm text-gray-200 mt-2 italic whitespace-pre-wrap p-2 rounded border" style={{ borderColor: 'rgba(254,237,0,0.3)', background: 'rgba(254,237,0,0.05)' }} data-testid="apt-prestation-detail">
                          {selectedAppointment.prestation_detail}
                        </p>
                      )}
                    </div>
                    {/* Prix caché pour les mécanos */}
                    {user?.role !== "mecano" && (
                      <div className="text-right">
                        <p className="text-xs text-gray-600">Prix</p>
                        <p className="text-xl font-bold text-green-400" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                          {selectedAppointment.price} €
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Status Actions */}
                <div className="space-y-1">
                  <p className="text-xs text-gray-600">Statut</p>
                  <div className="grid grid-cols-3 gap-1">
                    <Button
                      variant={selectedAppointment.status === "scheduled" ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleUpdateStatus(selectedAppointment.id, "scheduled")}
                      className={`text-xs ${selectedAppointment.status === "scheduled" 
                        ? "bg-[#FEED00] text-black hover:bg-[#FFF44F]" 
                        : "border-gray-700 text-gray-400 hover:border-[#FEED00] hover:text-[#FEED00]"}`}
                      data-testid="status-scheduled"
                    >
                      <Clock className="w-3 h-3 mr-1" />
                      Planifié
                    </Button>
                    <Button
                      variant={selectedAppointment.status === "completed" ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleUpdateStatus(selectedAppointment.id, "completed")}
                      className={`text-xs ${selectedAppointment.status === "completed" 
                        ? "bg-green-600 hover:bg-green-700 text-white" 
                        : "border-gray-700 text-gray-400 hover:border-green-500 hover:text-green-400"}`}
                      data-testid="status-completed"
                    >
                      <Check className="w-3 h-3 mr-1" />
                      Terminé
                    </Button>
                    <Button
                      variant={selectedAppointment.status === "cancelled" ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleUpdateStatus(selectedAppointment.id, "cancelled")}
                      className={`text-xs ${selectedAppointment.status === "cancelled" 
                        ? "bg-red-600 hover:bg-red-700 text-white" 
                        : "border-gray-700 text-gray-400 hover:border-red-500 hover:text-red-400"}`}
                      data-testid="status-cancelled"
                    >
                      <X className="w-3 h-3 mr-1" />
                      Annulé
                    </Button>
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-1 pt-2 border-t" style={{ borderColor: '#1A1A1A' }}>
                  {/* SMS Buttons - Side by Side */}
                  <div className="grid grid-cols-2 gap-1">
                    <Button
                      onClick={() => handleSendSMS(selectedAppointment.id)}
                      className="border-gray-700 text-gray-300 hover:bg-gray-800 text-xs px-2 h-8"
                      variant="outline"
                      size="sm"
                      data-testid="send-sms-btn"
                    >
                      <MessageSquare className="w-3 h-3 mr-1" />
                      SMS Rappel
                    </Button>
                    
                    <Button
                      onClick={() => handleSendReviewRequest(selectedAppointment.id)}
                      className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black font-semibold text-xs px-2 h-8"
                      size="sm"
                      disabled={selectedAppointment.review_request_sent}
                      data-testid="send-review-btn"
                    >
                      <Star className="w-3 h-3 mr-1" />
                      {selectedAppointment.review_request_sent ? "Avis ✓" : "Avis Google"}
                    </Button>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteAppointment(selectedAppointment.id)}
                    className="w-full text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs h-8"
                    data-testid="delete-apt-btn"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Supprimer le RDV
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
